import { useRequestPartsPriceMutation, useSavedProductMutation } from 'api';
import ConfirmationModal from 'common/components/ConfirmationModal';
import { ToastMsg } from 'common/components/Toast';
import { getPartialLeadTimeText, getStockText } from 'common/helpers/v1';
import styledTheme from 'common/theme';
import { Button, Input, Typography } from 'components';
import CustomLoader from 'components/CustomLoader';
import StyledTextArea from 'components/TextArea';
import CheckSuccess from 'icons/indexed-icons/CheckSuccess';
import Heart from 'icons/indexed-icons/Heart';
import Tags from 'icons/indexed-icons/Tags';
import Asterisk from 'pages/AlertsPage/Step3/Asterisk ';
import {
  ModalContent,
  RequestPriceHeader,
  SubHeading
} from 'pages/PartsCatalogPage/PartsShop.element';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Product } from 'types/parts';

type Props = {
  selectedNonePriceItem?: Product[];
  onSuccessCallback?: () => void;
};

const ConfirmationBox = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  .icon-heart {
    width: 2rem;
    height: 2rem;
  }
`;

const SavedProductBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
  flex-wrap: wrap;
  justify-content: center;
  .icon--heart {
    width: 16px;
    height: 16px;
  }
`;
function PriceRequestComponent({ selectedNonePriceItem, onSuccessCallback }: Props): JSX.Element {
  const [requestPartsPrice, { isLoading }] = useRequestPartsPriceMutation();
  const [comment, setComment] = useState('');
  const { t } = useTranslation(['fpns']);

  const [showContactUsForm, setShowContactUsForm] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleContactUsForm = () => {
    setShowContactUsForm(true);
  };

  const [partNameInputContent, partNumberInputContent] = useMemo(() => {
    if (!selectedNonePriceItem?.length) return ['', ''];

    const names: string[] = [];
    const skus: string[] = [];

    selectedNonePriceItem.forEach((part) => {
      names.push(part.description.trim());
      skus.push(part.sku);
    });

    return [names.join(', '), skus.join(', ')];
  }, [selectedNonePriceItem]);

  const handleSubmitGetPrice = async () => {
    if (isLoading) return;
    const partData =
      selectedNonePriceItem?.map((part) => ({
        part_id: part.id,
        product: part.description,
        sku: part.sku,
        stock: getStockText(part, t),
        avgleadtime: getPartialLeadTimeText(part.leadTime, t),
        quantity: part.quantity
      })) || [];
    const params = {
      parts: partData,
      comments: comment
    };
    const resp = await requestPartsPrice(params || []);
    if (resp.error) {
      return ToastMsg({
        heading: 'Error',
        message: 'Price request failed',
        type: 'error'
      });
    }
    setRequestId(resp.data.requestId || '');
    setIsSuccess(true);
    onSuccessCallback?.();
    setComment('');
  };
  const handleCloseModal = () => {
    setShowContactUsForm(false);
    setIsSuccess(false);
  };

  const goToSavedProductsList = () => {
    document.getElementById('saved-icon')?.click();
    setShowContactUsForm(false);
    setIsSuccess(false);
  };
  return (
    <>
      <Button
        disabled={!selectedNonePriceItem?.length}
        variant="text"
        onClick={handleContactUsForm}
        marginLeft="0"
      >
        <Tags color={selectedNonePriceItem?.length ? styledTheme.colors.jbtBlue : '#0076cc60'} />
        <span style={{ marginLeft: '.5rem' }}>Get Parts Price</span>
      </Button>
      {showContactUsForm && (
        <ConfirmationModal
          primaryBtnText={t(isSuccess ? 'close' : 'Submit') as string}
          secondaryBtnText={t(isSuccess ? 'Go to Saved Product List' : 'cancel') as string}
          margin={'0'}
          title={<RequestPriceHeader>Contact Us</RequestPriceHeader>}
          isVisible={showContactUsForm}
          height="65%"
          size="small"
          variant="default"
          onCancelCallback={isSuccess ? goToSavedProductsList : handleCloseModal}
          onConfirmCallback={
            isSuccess
              ? () => {
                  setShowContactUsForm(false);
                  setIsSuccess(false);
                }
              : handleSubmitGetPrice
          }
          isConfirmPrompt
        >
          <ModalContent>
            <SubHeading>
              <Typography className="sub-text">
                Please fill out the form below to request pricing and availability information.
              </Typography>
              {/* <hr /> */}
            </SubHeading>
            {isLoading ? (
              <CustomLoader size="3rem" padding="3rem" />
            ) : isSuccess ? (
              <ConfirmationBox>
                <CheckSuccess />
                <Typography size={'1.25rem'} weight="semi-bold" color={styledTheme.colors.gray800}>
                  Request ID - #{requestId}
                </Typography>
                <Typography size={'.8rem'} color={styledTheme.colors.gray800} weight="semi-bold">
                  A representative will get back to you
                </Typography>
                <SavedProductBtn>
                  <div>The item(s) have been added to the</div>
                  <br/>
                  <div style={{ color: styledTheme.colors.jbtBlue }}>{t('saved_product_list')}</div>
                  <Heart />
                </SavedProductBtn>
              </ConfirmationBox>
            ) : (
              <>
                <div>
                  <Typography color={styledTheme.colors.gray600} weight="semi-bold" mb={'.3rem'}>
                    Reason <Asterisk />
                  </Typography>
                  <Input value={'Part price & availability'} variant="disabled" />
                </div>
                <div>
                  <Typography color={styledTheme.colors.gray600} weight="semi-bold" mb={'.3rem'}>
                    Part Name <Asterisk />
                  </Typography>
                  <Input value={partNameInputContent} variant="disabled" />
                </div>
                <div>
                  <Typography color={styledTheme.colors.gray600} weight="semi-bold" mb={'.3rem'}>
                    Part Number <Asterisk />
                  </Typography>

                  <Input value={partNumberInputContent} variant="disabled" />
                </div>

                <div>
                  <label>
                    <Typography weight="semi-bold" color={styledTheme.colors.black} mb={'.2rem'}>
                      Comments
                    </Typography>
                  </label>
                  <StyledTextArea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={6}
                  />
                </div>
              </>
            )}
          </ModalContent>
        </ConfirmationModal>
      )}
    </>
  );
}
export default PriceRequestComponent;
